import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    useTheme,
    Button,
    useMediaQuery,
    Menu,
    MenuItem,
    ListItemText,
    Badge,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { FlexDiv } from '../FlexDiv'
import { AppRoutes } from '../../helpers/Routes'
import HomeIcon from '@mui/icons-material/Home'
import PaymentsIcon from '@mui/icons-material/Payments'
import LogoutIcon from '@mui/icons-material/Logout'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import BuildIcon from '@mui/icons-material/Build'
import ConstructionIcon from '@mui/icons-material/Construction'
import ArticleIcon from '@mui/icons-material/Article'
import EventIcon from '@mui/icons-material/Event'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { useAppDispatch } from '../../hooks'
import { useTenantMessages } from '../../hooks/useTenantMessages'
import { selectPortalMembership, setRootUser } from '../../store/User/action'
import { LocalStorage } from '../../helpers'
import { Lease } from '../../types/lease'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

import { LeasingMode } from '../../types/leasingContext'
import AssignmentIcon from '@mui/icons-material/Assignment'

interface NavItem {
    icon: React.ReactNode
    text: string
    path: string
    onClick?: () => void
    subNavItems?: NavItem[]
}

interface SideNavProps {
    selectedLease?: Lease | null
    onMobileNavClick?: () => void
    mode?: LeasingMode
}

export const SideNavWidth = 180

export const SideNav: React.FC<SideNavProps> = ({
    selectedLease,
    onMobileNavClick,
    mode = 'resident',
}) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useAppDispatch()
    const { unreadCount, getUnreadCount } = useTenantMessages()
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    useEffect(() => {
        getUnreadCount()
        intervalRef.current = setInterval(getUnreadCount, 45000)
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    useEffect(() => {
        getUnreadCount()
    }, [location.pathname])

    const handleLogout = () => {
        dispatch(setRootUser(undefined))
        localStorage.removeItem(LocalStorage.EZTURN_TENANT_TOKEN)
        if (isMobile && onMobileNavClick) {
            onMobileNavClick()
        }
    }

    const isApplicant = mode === 'applicant'
    const leasingContext = useSelector(
        (state: RootState) => state.user.leasingContext,
    )
    const memberships = leasingContext?.memberships ?? []
    const canSwitch = memberships.length > 1
    const [propertyMenu, setPropertyMenu] = useState<null | HTMLElement>(null)
    const selectedMembership =
        memberships.find((row) => row.id === leasingContext?.apartment?.id) ??
        memberships[0]
    const propertyName =
        selectedMembership?.apartment.name ||
        (isApplicant
            ? leasingContext?.apartment?.name || 'Application'
            : selectedLease?.apartment_name || '-')
    const propertySubtitle =
        selectedMembership?.subtitle ||
        (isApplicant
            ? leasingContext?.season?.name || 'Applicant'
            : `${selectedLease?.area?.unit_name || ''} · ${
                  selectedLease?.area?.area_label || ''
              }`)

    const handleSelectProperty = async (apartmentId: string) => {
        setPropertyMenu(null)
        if (apartmentId === leasingContext?.apartment?.id) {
            return
        }
        const context = await dispatch(selectPortalMembership(apartmentId))
        navigate(AppRoutes.home)
        if (isMobile && onMobileNavClick) {
            onMobileNavClick()
        }
        return context
    }

    const messagesNav: NavItem = {
        icon: (
            <Badge
                badgeContent={unreadCount}
                color="secondary"
                invisible={unreadCount === 0}
                sx={{
                    '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: 16,
                        minWidth: 16,
                    },
                }}
            >
                <MailOutlineIcon />
            </Badge>
        ),
        text: 'Messages',
        path: AppRoutes.messages,
    }

    const mainNavItems: NavItem[] = isApplicant
        ? [
              { icon: <HomeIcon />, text: 'Home', path: AppRoutes.home },
              {
                  icon: <AssignmentIcon />,
                  text: 'Application',
                  path: AppRoutes.applicant,
              },
              {
                  icon: <ArticleIcon />,
                  text: 'Documents',
                  path: AppRoutes.documents,
              },
              messagesNav,
          ]
        : [
              { icon: <HomeIcon />, text: 'Home', path: AppRoutes.home },
              { icon: <BuildIcon />, text: 'Maintenance', path: '/maintenance' },
              { icon: <ConstructionIcon />, text: 'Damages', path: AppRoutes.damages },
              { icon: <ArticleIcon />, text: 'Documents', path: AppRoutes.documents },
              { icon: <EventIcon />, text: 'Events', path: AppRoutes.events },
              { icon: <FactCheckIcon />, text: 'Inspections', path: AppRoutes.inspections },
              { icon: <ReceiptLongIcon />, text: 'Invoices', path: AppRoutes.invoices },
              messagesNav,
          ]

    if (!isApplicant && selectedLease?.allow_rent) {
        mainNavItems.push({
            icon: <PaymentsIcon />,
            text: 'Payments',
            path: AppRoutes.managePayments,
        })
    }

    const NavButton: React.FC<{ item: NavItem }> = ({ item }) => {
        const isActive =
            location.pathname === item.path ||
            (item.path === AppRoutes.inspections &&
                location.pathname.startsWith('/inspections')) ||
            (item.path === AppRoutes.messages &&
                location.pathname.startsWith('/messages')) ||
            (item.path === AppRoutes.events &&
                location.pathname.startsWith('/events')) ||
            (item.subNavItems &&
                item.subNavItems.some(
                    (subItem) => location.pathname === subItem.path,
                ))

        const handleClick = () => {
            if (item.onClick) {
                item.onClick()
            } else {
                navigate(item.path)
            }
            if (isMobile && onMobileNavClick) {
                onMobileNavClick()
            }
        }

        return (
            <Button
                startIcon={item.icon}
                onClick={handleClick}
                style={{
                    justifyContent: 'flex-start',
                    padding: theme.spacing(0.75),
                    color: isActive ? '#fff' : '#8b949e',
                    backgroundColor: isActive ? '#2a2f35' : 'transparent',
                    borderRadius: 8,
                    textTransform: 'none',
                    width: '100%',
                }}
            >
                {item.text}
            </Button>
        )
    }

    return (
        <FlexDiv
            direction="column"
            style={{
                width: SideNavWidth,
                height: '100%',
                position: 'fixed',
                top: 0,
                left: 0,
                backgroundColor: '#1a1d21',
                borderRight: '1px solid rgba(151, 147, 147, 0.79)',
                padding: theme.spacing(2),
                overflowY: 'auto',
                zIndex: 1000,
            }}
        >
            {/* Unit Info */}
            <FlexDiv
                direction="column"
                style={{
                    marginBottom: theme.spacing(4),
                    alignItems: 'stretch',
                    width: '100%',
                }}
            >
                <Button
                    onClick={(event) => {
                        if (!canSwitch) return
                        setPropertyMenu(event.currentTarget)
                    }}
                    endIcon={canSwitch ? <KeyboardArrowDownIcon /> : undefined}
                    style={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        color: '#fff',
                        backgroundColor: '#2a2f35',
                        borderRadius: 8,
                        padding: theme.spacing(1),
                        width: '100%',
                        cursor: canSwitch ? 'pointer' : 'default',
                    }}
                >
                    <FlexDiv direction="column" style={{ alignItems: 'flex-start', minWidth: 0 }}>
                        <span
                            style={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                textAlign: 'left',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 140,
                            }}
                        >
                            {propertyName}
                        </span>
                        <span
                            style={{
                                color: '#8b949e',
                                fontSize: '0.75rem',
                                textAlign: 'left',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 140,
                            }}
                        >
                            {propertySubtitle}
                        </span>
                    </FlexDiv>
                </Button>
                <Menu
                    anchorEl={propertyMenu}
                    open={Boolean(propertyMenu)}
                    onClose={() => setPropertyMenu(null)}
                    PaperProps={{
                        sx: {
                            backgroundColor: '#2a2f35',
                            color: '#fff',
                            minWidth: 180,
                        },
                    }}
                >
                    {memberships.map((membership) => (
                        <MenuItem
                            key={membership.id}
                            selected={membership.id === leasingContext?.apartment?.id}
                            onClick={() => handleSelectProperty(membership.id)}
                        >
                            <ListItemText
                                primary={membership.apartment.name}
                                secondary={`${membership.mode_label} · ${membership.subtitle}`}
                                primaryTypographyProps={{ color: '#fff' }}
                                secondaryTypographyProps={{ color: '#8b949e' }}
                            />
                        </MenuItem>
                    ))}
                </Menu>
            </FlexDiv>

            {/* Main Navigation Items */}
            <FlexDiv direction="column">
                {mainNavItems.map((item) => (
                    <NavButton key={item.text} item={item} />
                ))}
                <NavButton
                    item={{
                        icon: <PersonOutlineIcon />,
                        text: 'Account',
                        path: AppRoutes.account,
                    }}
                />
                <NavButton
                    item={{
                        icon: <LogoutIcon />,
                        text: 'Logout',
                        path: '',
                        onClick: handleLogout,
                    }}
                />
            </FlexDiv>
        </FlexDiv>
    )
}
